import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { del, HTTPError, patch, post } from "common/helpers/HTTP";
import type {
  CrmIntegrationSettingsItem,
  CrmType,
  EmailOutreachType,
  IntegrationSettings,
  Rb2bIntegration,
} from "common/types";

async function removeIntegrationRequest(
  workspaceId: string,
  integrationKey: keyof IntegrationSettings,
): Promise<string> {
  return (await del(`workspaces/${workspaceId}/integrations/${integrationKey}`))
    .install_url;
}

async function updateCrmIntegrationRequest(
  workspaceId: string,
  crmType: CrmType,
  updates: CrmIntegrationSettingsItem,
) {
  await patch(`workspaces/${workspaceId}/integrations/${crmType}`, updates);
}

async function connectEmailOutreachIntegrationRequest(
  workspaceId: string,
  type: EmailOutreachType,
  api_key: string,
): Promise<string> {
  return (
    await post(`workspaces/${workspaceId}/integrations/${type}`, { api_key })
  ).workspace_name;
}

async function connectRb2bRequest(
  workspaceId: string,
  campaignId: string,
): Promise<Rb2bIntegration> {
  const response = await post(
    `workspaces/${workspaceId}/integrations/rb2b/campaigns/${campaignId}`,
  );

  return response;
}

export default function useIntegrationMutations() {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const queryClient = useQueryClient();

  const {
    mutateAsync: updateCrmIntegration,
    isPending: isUpdatingCrmIntegration,
  } = useMutation({
    mutationFn: ({
      updates,
      crmType,
    }: {
      updates: CrmIntegrationSettingsItem;
      crmType: CrmType;
    }) => updateCrmIntegrationRequest(workspaceId, crmType, updates),
    onSuccess: (_, { updates, crmType }) => {
      queryClient.setQueryData<IntegrationSettings>(
        getQueryKeys(workspaceId).integrationSettingsKey,
        (prevSettings) => ({
          ...prevSettings,
          [crmType]: updates,
        }),
      );
    },
    onError: () => toast.error("Failed to update integration"),
  });

  const { mutateAsync: removeIntegration, isPending: isRemovingIntegration } =
    useMutation({
      mutationFn: (integrationKey: keyof IntegrationSettings) =>
        removeIntegrationRequest(workspaceId, integrationKey),
      onSuccess: (installUrl, integrationKey) => {
        queryClient.setQueryData<IntegrationSettings>(
          getQueryKeys(workspaceId).integrationSettingsKey,
          (prevSettings) => ({
            ...prevSettings,
            [integrationKey]:
              integrationKey === "hubspot" || integrationKey === "pipedrive"
                ? { install_url: installUrl }
                : null,
          }),
        );

        toast.success("Integration removed");
      },
      onError: () => toast.error("Failed to remove integration"),
    });

  const {
    mutateAsync: connectEmailOutreachIntegration,
    isPending: isConnectingEmailOutreachIntegration,
  } = useMutation({
    mutationFn: ({
      type,
      apiKey,
    }: {
      type: EmailOutreachType;
      apiKey: string;
    }) => connectEmailOutreachIntegrationRequest(workspaceId, type, apiKey),
    onSuccess: (workspaceName, { type, apiKey }) => {
      queryClient.setQueryData<IntegrationSettings>(
        getQueryKeys(workspaceId).integrationSettingsKey,
        (prevSettings) => ({
          ...prevSettings,
          ...(type === "instantly"
            ? {
                instantly: {
                  workspace_name: workspaceName,
                },
              }
            : {
                smartlead: {
                  api_key: apiKey,
                },
              }),
        }),
      );

      toast.success(`Connected with ${type}`);
    },
    onError: (err) =>
      toast.error(
        err instanceof HTTPError && err.code === 401 && err.serverMessage
          ? err.serverMessage
          : "Failed to connect integration",
      ),
  });

  const { mutateAsync: connectRb2b, isPending: isConnectingRb2b } = useMutation(
    {
      mutationFn: (campaignId: string) =>
        connectRb2bRequest(workspaceId, campaignId),
      onSuccess: (rb2bIntegration) => {
        queryClient.setQueryData<IntegrationSettings>(
          getQueryKeys(workspaceId).integrationSettingsKey,
          (prevSettings) => ({
            ...prevSettings,
            rb2b: rb2bIntegration,
          }),
        );
      },
      onError: () => toast.error("Failed to connect RB2B"),
    },
  );

  return {
    updateCrmIntegration,
    isUpdatingCrmIntegration,
    removeIntegration,
    isRemovingIntegration,
    connectEmailOutreachIntegration,
    isConnectingEmailOutreachIntegration,
    connectRb2b,
    isConnectingRb2b,
  };
}
